import { useWatch } from 'react-hook-form';
import CheckboxField from 'components/forms/checkboxField/CheckboxField';
import TextAreaField from 'components/forms/textAreaField/TextAreaField';
import TextField from 'components/forms/textField/TextField';
import { Channel } from 'models/Channels';
import { PropertyBusinessType } from 'models/Properties';
import usePropertySettingsInitData from '../usePropertySettingsInitData';
import { DescriptionFields } from './PropertySettingsDescriptionsTab.types';
import LabelWithTooltip from './form/LabelWithTooltip';
import {
  ChannelToFieldVisibilityMapping,
  FieldVisibility,
  isRequiredAtPropertyLevel,
} from './PropertySettingsDescriptionsTabFormFields.utils';
import {
  BOOKING_DOT_COM_SUMMARY_MAX_CHARS,
  DESCRIPTION_FIELD_MAX_CHARS,
  NAME_MAX_CHARS,
  PropertySettingsDescriptionsTabFormValues,
  SHORT_SUMMARY_MAX_CHARS,
  SUMMARY_MAX_CHARS,
} from './PropertySettingsDescriptionsTab.schema';

interface PropertySettingsDescriptionsTabFormFieldsProps {
  activeChannels: Channel[];
  activeChannelsAtPropertyLevel: Channel[];
}

const PropertySettingsDescriptionsTabFormFields = ({
  activeChannels,
  activeChannelsAtPropertyLevel,
}: PropertySettingsDescriptionsTabFormFieldsProps) => {
  const { property } = usePropertySettingsInitData();
  const { isApplyADifferentLongDescriptionEnabled } =
    useWatch<PropertySettingsDescriptionsTabFormValues>();
  const channelVisibility = activeChannels
    .map((channel) => ChannelToFieldVisibilityMapping[channel])
    .filter(Boolean);

  const isRequired = (fieldName: DescriptionFields) => {
    return isRequiredAtPropertyLevel(activeChannelsAtPropertyLevel, fieldName);
  };

  const isVisible = (fieldName: DescriptionFields) => {
    if (isRequired(fieldName)) {
      return true;
    }

    return channelVisibility.some((fieldVisibility) => {
      if (!fieldVisibility) {
        return false;
      }

      return [
        FieldVisibility.VISIBLE,
        FieldVisibility.VISIBLE_AND_REQUIRED,
      ].includes(fieldVisibility[fieldName]);
    });
  };

  const isNotChildProperty =
    property?.businessType !== PropertyBusinessType.SUB_UNIT &&
    property?.businessType !== PropertyBusinessType.UNIT_TYPE;

  return (
    <>
      {isVisible('name') && (
        <TextField
          label={<LabelWithTooltip fieldName="name" />}
          name="name"
          maxLength={NAME_MAX_CHARS}
          colSmInput={12}
          labelOnTop
          required={isRequired('name')}
        />
      )}
      {isVisible('shortSummary') && (
        <TextAreaField
          label={<LabelWithTooltip fieldName="shortSummary" />}
          name="shortSummary"
          maxLength={SHORT_SUMMARY_MAX_CHARS}
          colSmInput={12}
          rows={3}
          labelOnTop
          required={isRequired('shortSummary')}
        />
      )}
      {isVisible('summary') && (
        <TextAreaField
          label={<LabelWithTooltip fieldName="summary" />}
          name="summary"
          maxLength={SUMMARY_MAX_CHARS}
          colSmInput={12}
          rows={6}
          labelOnTop
          required={isRequired('summary')}
        />
      )}
      {isVisible('isApplyADifferentLongDescriptionEnabled') &&
        isNotChildProperty && (
          <CheckboxField
            className="is-apply-a-different-long-description"
            name="isApplyADifferentLongDescriptionEnabled"
          >
            <LabelWithTooltip fieldName="isApplyADifferentLongDescriptionEnabled" />
          </CheckboxField>
        )}
      {isVisible('bookingDotComSummary') &&
        isApplyADifferentLongDescriptionEnabled && (
          <TextAreaField
            label={<LabelWithTooltip fieldName="bookingDotComSummary" />}
            name="bookingDotComSummary"
            maxLength={BOOKING_DOT_COM_SUMMARY_MAX_CHARS}
            colSmInput={12}
            rows={6}
            labelOnTop
            required
          />
        )}
      {isVisible('notes') && (
        <TextAreaField
          label={<LabelWithTooltip fieldName="notes" />}
          name="notes"
          maxLength={DESCRIPTION_FIELD_MAX_CHARS}
          maxCharacters={5_000}
          colSmInput={12}
          rows={12}
          labelOnTop
        />
      )}
      {isVisible('interaction') && (
        <TextAreaField
          label={<LabelWithTooltip fieldName="interaction" />}
          name="interaction"
          maxLength={DESCRIPTION_FIELD_MAX_CHARS}
          maxCharacters={5_000}
          colSmInput={12}
          rows={12}
          labelOnTop
        />
      )}
      {isVisible('neighbourhood') && (
        <TextAreaField
          label={<LabelWithTooltip fieldName="neighbourhood" />}
          name="neighbourhood"
          maxLength={DESCRIPTION_FIELD_MAX_CHARS}
          maxCharacters={2_000}
          colSmInput={12}
          rows={12}
          labelOnTop
        />
      )}
      {isVisible('access') && (
        <TextAreaField
          label={<LabelWithTooltip fieldName="access" />}
          name="access"
          maxLength={DESCRIPTION_FIELD_MAX_CHARS}
          maxCharacters={5_000}
          colSmInput={12}
          rows={12}
          labelOnTop
        />
      )}
      {isVisible('space') && (
        <TextAreaField
          label={<LabelWithTooltip fieldName="space" />}
          name="space"
          maxLength={DESCRIPTION_FIELD_MAX_CHARS}
          maxCharacters={5_000}
          colSmInput={12}
          rows={12}
          labelOnTop
        />
      )}
      {isVisible('transit') && (
        <TextAreaField
          label={<LabelWithTooltip fieldName="transit" />}
          name="transit"
          maxLength={DESCRIPTION_FIELD_MAX_CHARS}
          maxCharacters={5_000}
          colSmInput={12}
          rows={12}
          labelOnTop
        />
      )}
      {isVisible('houseManual') && (
        <TextAreaField
          label={<LabelWithTooltip fieldName="houseManual" />}
          name="houseManual"
          maxLength={DESCRIPTION_FIELD_MAX_CHARS}
          maxCharacters={5_000}
          colSmInput={12}
          rows={12}
          labelOnTop
        />
      )}
    </>
  );
};

export default PropertySettingsDescriptionsTabFormFields;
